import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/store';
import { pricesSelector } from 'features/billing/prices/pricesSlice';
import { IPriceListItem } from 'features/billing/prices/pricesTypes';
import { subscriptionInfoSelector } from 'features/billing/subscription/subscriptionSlice';
import { IAddUpdateSubscriptionData, SubscriptionStatus } from 'features/billing/subscription/subscriptionTypes';
import { updateSubscriptionInfo } from 'features/billing/subscription/subscriptionActions';
import { customerInfoSelector } from 'features/billing/customer/customerSlice';
import { couponInfoSelector } from 'features/billing/couponInfo/couponInfoSlice';
import { getUpcomingInvoice, getUpcomingInvoiceForSubscription } from 'features/billing/upcomingInvoice/upcomingInvoiceActions';
import { isLoadingUpcomingInvoiceSelector, upcomingInvoiceSelector } from 'features/billing/upcomingInvoice/upcomingInvoiceSlice';
import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import TermsOfUse from 'components/shared/TermsOfUse';
import PriceBreakdown from 'components/billing/SubscriptionDetails/PriceBreakdown/PriceBreakdown';
import styles from './Plans.module.scss';
import { billingFormsSubscriptionInfoSelector } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoSlice';
import { setSubscriptionFormInfo } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoActions';
import { currenciesListSelector } from 'features/currencies/currenciesSlice';

interface IProduct {
  id: string;
  name: string;
  description: string;
  featureList: string[];
  prices: IPriceListItem[];
}

const SubscriptionPlans: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currencies = useSelector(currenciesListSelector)

  const prices = useSelector(pricesSelector);
  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const customerInfo = useSelector(customerInfoSelector);
  const couponInfo = useSelector(couponInfoSelector);
  const upcomingInvoice = useSelector(upcomingInvoiceSelector);
  const upcomingInvoiceIsLoading = useSelector(isLoadingUpcomingInvoiceSelector);

  const subscriptionFormInfo = useSelector(billingFormsSubscriptionInfoSelector);

  const [termsOfUseAccepted, setTermsOfUseAccepted] = useState(false);
  const [updatePlanModalOpen, setUpdatePlanModalOpen] = useState(false);
  const [priceBreakdownIsShown, setPriceBreakdownIsShown] = useState(false);
  const [subscriptionUpdateInfo, setSubscriptionUpdateInfo] = useState(
    {
      priceId: subscriptionInfo!.priceId,
      currency: subscriptionInfo!.currency,
      interval: subscriptionInfo!.interval,
    }
  );
  
  const getCurrencySymbol = (currencyCode: string) => currencies.find(x => x.code.toLowerCase() === currencyCode.toLowerCase())?.symbol ?? currencyCode
  const currentCurrencySymbol = getCurrencySymbol(subscriptionUpdateInfo.currency)

  const isAnnual = subscriptionUpdateInfo.interval === 'year';
  const subscriptionIsActive = subscriptionInfo!.status === SubscriptionStatus.Active;
  const subscriptionIsTrialing = subscriptionInfo!.status === SubscriptionStatus.Trialing;
  const isActiveOrTrialing = subscriptionIsActive || subscriptionIsTrialing;

  const filteredPrices = prices
    .filter((price) => price.currency === subscriptionUpdateInfo.currency)
    .sort((a, b) => a.productName.localeCompare(b.productName));

  const productPrices = filteredPrices.reduce((acc, price) => {
    if (!acc[price.productId]) {
      acc[price.productId] = {
        id: price.productId,
        name: price.productName,
        description: price.productDescription,
        featureList: price.productFeatureList,
        prices: [],
      };
    }
    acc[price.productId].prices.push(price);
    return acc;
  }, {} as Record<string, IProduct>);

  const handlePlanUpdate = () => {
    const updateSubscriptionData: IAddUpdateSubscriptionData = {
      subscriptionId: subscriptionInfo!.id,
      priceId: subscriptionUpdateInfo.priceId ?? subscriptionInfo!.priceId,
      quantity: subscriptionInfo!.quantity,
      couponId: subscriptionInfo!.couponId,
    };

    dispatch(updateSubscriptionInfo(updateSubscriptionData));
    dispatch(setSubscriptionFormInfo({
      ...subscriptionFormInfo!,
      productId: prices.find(price => price.id === updateSubscriptionData.priceId)!.productId,
      quantity: subscriptionInfo!.quantity,
    }))
    setTermsOfUseAccepted(false);
  };

  const handlePlanButtonClick = (productId: string) => {
    const priceId = prices.find(
      (price) =>
        price.productId === productId &&
        price.interval === subscriptionUpdateInfo.interval &&
        price.currency === subscriptionUpdateInfo.currency
    )?.id ?? subscriptionInfo!.priceId;
    setSubscriptionUpdateInfo({ ...subscriptionUpdateInfo, priceId: priceId })

    if (isActiveOrTrialing) {
      if (!subscriptionIsTrialing) {
        dispatch(getUpcomingInvoiceForSubscription({
          subscriptionId: subscriptionInfo!.id,
          priceId: priceId,
          quantity: subscriptionInfo!.quantity,
        }));
      }
      setUpdatePlanModalOpen(true);
      return;
    }

    dispatch(getUpcomingInvoice({
      priceId: subscriptionUpdateInfo.priceId,
      quantity: subscriptionInfo!.quantity,
      address: customerInfo.address,
      couponId: subscriptionInfo!.couponId,
    }))

    dispatch(setSubscriptionFormInfo({
      ...subscriptionFormInfo,
      productId: productId,
      interval: subscriptionUpdateInfo.interval,
      currency: subscriptionUpdateInfo.currency,
    }))
    navigate('/payment')
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toggleContainer}>
        <label
          htmlFor="annual"
          className={`${isAnnual ? styles.active : ""}`}
        >
          <input
            id="annual"
            type="radio"
            name="pricing"
            checked={isAnnual}
            onChange={() => setSubscriptionUpdateInfo({ ...subscriptionUpdateInfo, interval: 'year' })}
          />
          Annual
          <span className={styles.badge}>30% free</span>
        </label>
        <label
          htmlFor="monthly"
          className={`${!isAnnual ? styles.active : ""}`}
        >
          <input
            id="monthly"
            type="radio"
            name="pricing"
            checked={!isAnnual}
            onChange={() => setSubscriptionUpdateInfo({ ...subscriptionUpdateInfo, interval: 'month' })}
          />
          Monthly
        </label>
      </div>

      <div className={styles.plans}>
        {Object.values(productPrices).map((product) => (
          <div
            key={product.id}
            className={`
              ${styles.plan} 
              ${product.id === subscriptionInfo!.productId && styles.currentPlanCard
              }`}
          >
            <h1 className={styles.textAlignCenter}>{product.name}</h1>
            <ul className={styles.priceList}>
              {product.prices
                .sort((p1, p2) => p2.name.localeCompare(p1.name))
                .map((price) => (
                  <li
                    key={price.id}
                    className={`
                      ${styles.mb4} 
                      ${price.interval !== subscriptionUpdateInfo.interval && styles.strikethrough}
                    `}
                  >
                    {getCurrencySymbol(price.currency)}
                    {price.amount} / seat / {price.interval}
                  </li>
                ))}
            </ul>

            <button
              className={styles.upgradePlan}
              onClick={() => handlePlanButtonClick(product.id)}
              disabled={
                (isActiveOrTrialing) &&
                product.id === subscriptionInfo!.productId &&
                subscriptionUpdateInfo.interval === subscriptionInfo!.interval &&
                subscriptionUpdateInfo.currency === subscriptionInfo!.currency
              }
            >
              {!isActiveOrTrialing
                ? 'Choose plan'
                : product.id === subscriptionInfo!.productId &&
                  subscriptionUpdateInfo.interval === subscriptionInfo!.interval &&
                  subscriptionUpdateInfo.currency === subscriptionInfo!.currency
                  ? 'Subscribed'
                  : 'Update'}
            </button>

            <div className={styles.flexCol}>
              <p className={styles.title}>Includes:</p>
              <ul className={styles.features}>
                {product.featureList.map((feature, index) => (
                  <li className={styles.featureItem} key={index}>
                    <span className={styles.tick}>
                      <i className={styles.check}></i>
                    </span>
                    <p>{feature}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <Modal
        title='Update Plan'
        open={updatePlanModalOpen && (subscriptionUpdateInfo.priceId !== subscriptionInfo!.priceId)}
        onClose={() => {
          setUpdatePlanModalOpen(false);
          setTermsOfUseAccepted(false);
        }}
      >
        <div className={styles.updatePlanModalContainer}>
          <div className={styles.spaceBetween}>
            <p>New plan</p>
            <p>{isAnnual ? "Annual" : "Monthly"} billing</p>
          </div>

          <p className={styles.title}>
            {filteredPrices.find((price) => price.id === subscriptionUpdateInfo.priceId)?.name}
          </p>

          <div className={styles.spaceBetween}>
            <p>
              Subscription seats: {subscriptionInfo!.quantity} x{' '}
              {getCurrencySymbol(subscriptionUpdateInfo.currency)}
              {filteredPrices.find((price) => price.id === subscriptionUpdateInfo.priceId)?.amount}
            </p>
            <p>
              {getCurrencySymbol(subscriptionUpdateInfo.currency)}
              {(
                subscriptionInfo!.quantity *
                (prices.find(
                  (price) => price.id === subscriptionUpdateInfo.priceId
                )?.amount ?? 0)
              ).toFixed(2)}
            </p>
          </div>

          <p className={styles.title}>
            <span>Total price:{' '}</span>

            {upcomingInvoiceIsLoading ?
              <PulseLoader color="#0168fa" size={12} margin={7} speedMultiplier={1} />
              :
              subscriptionIsTrialing ?
                <span>{currentCurrencySymbol}0 - free trial</span>
                :
                (upcomingInvoice.discountAmount > 0 || couponInfo) ?
                  <p>
                    {upcomingInvoice.discountAmount > 0 &&
                      <span className={styles.strikethrough}>
                        {currentCurrencySymbol}{upcomingInvoice.subtotal}
                      </span>
                    }

                    <span className={styles.fontWeightBold}>
                      {" "} {currentCurrencySymbol}{upcomingInvoice.amountDue}
                    </span>

                    {(upcomingInvoice.tax ?? 0) > 0 &&
                      <span>{" "}({currentCurrencySymbol}{upcomingInvoice.tax} Tax)</span>
                    }
                    <span>{" "}- discount coupon applied</span>
                  </p>
                  :
                  <p>
                    <span>{currentCurrencySymbol}{upcomingInvoice.amountDue}</span>

                    {((upcomingInvoice.tax ?? 0) > 0 && upcomingInvoice.amountDue > 0) &&
                      <span>{" "}({currentCurrencySymbol}{upcomingInvoice.tax} Tax)</span>
                    }
                  </p>
            }
          </p>

          {!subscriptionIsTrialing &&
            <>
              <Button
                variant="default"
                onClick={() => setPriceBreakdownIsShown(true)}
                style={{ marginBottom: 24 }}
              >
                View price breakdown
              </Button>

              <TermsOfUse
                termsAccepted={termsOfUseAccepted}
                setTermsAccepted={setTermsOfUseAccepted}
              />
            </>
          }

          <div className={styles.formFooter}>
            <Button
              variant="primary"
              onClick={handlePlanUpdate}
              disabled={!termsOfUseAccepted && !subscriptionIsTrialing}
            >
              {subscriptionIsActive ? 'Update and pay' : 'Update'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        title={'Price breakdown'}
        open={priceBreakdownIsShown}
        onClose={() => setPriceBreakdownIsShown(false)}
      >
        <PriceBreakdown newQuantity={subscriptionInfo!.quantity} />
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
